import { makeImmutable } from "../../core/makeImmutable";
import { RSet } from "../../collections";
import { InternalParameterMultiSet } from "../ParameterMultiSet";
/**
 * A single combination of different internal parameters.
 *
 * For example: "splitToHalves: true, fulfillmentMethod: delivery"
 *
 * Internal (built in) parameters are:
 *
 * "fulfillmentMethod" - How the order will be fulfilled
 *
 * "splitToHalves" - Is the product split?
 *
 * "orderOrigin" - Where was the order made
 */
export class InternalParameterSet {
    constructor(params) {
        this.fulfillmentMethod = params.fulfillmentMethod;
        this.splitToHalves = params.splitToHalves;
        this.orderOrigin = params.orderOrigin;
        makeImmutable(this);
    }
    static empty() {
        return new InternalParameterSet({
            fulfillmentMethod: null,
            splitToHalves: null,
            orderOrigin: null,
        });
    }
    get isEmpty() {
        return (this.fulfillmentMethod === null &&
            this.splitToHalves === null &&
            this.orderOrigin === null);
    }
    clone() {
        return new InternalParameterSet({
            fulfillmentMethod: this.fulfillmentMethod,
            splitToHalves: this.splitToHalves,
            orderOrigin: this.orderOrigin,
        });
    }
    unifyFulfillmentMethod(other) {
        if (this.fulfillmentMethod === null || other === null) {
            return null;
        }
        if (this.fulfillmentMethod === other) {
            return {
                type: "success",
                matchingParameters: RSet.singleton(this.fulfillmentMethod),
                value: this.fulfillmentMethod,
            };
        }
        return {
            type: "failure",
            matchingParameters: RSet.empty(),
            incompatibleParameters: RSet.singleton(other),
        };
    }
    unifySplitToHalves(other) {
        if (this.splitToHalves === null || other === null) {
            return null;
        }
        if (this.splitToHalves === other) {
            return {
                type: "success",
                matchingParameters: RSet.singleton(this.splitToHalves),
                value: this.splitToHalves,
            };
        }
        return {
            type: "failure",
            matchingParameters: RSet.empty(),
            incompatibleParameters: RSet.singleton(other),
        };
    }
    unifyOrderOrigin(other) {
        if (this.orderOrigin === null || other === null) {
            return null;
        }
        if (this.orderOrigin === other) {
            return {
                type: "success",
                matchingParameters: RSet.singleton(this.orderOrigin),
                value: this.orderOrigin,
            };
        }
        return {
            type: "failure",
            matchingParameters: RSet.empty(),
            incompatibleParameters: RSet.singleton(other),
        };
    }
    unify(other) {
        const fulfillmentMethod = this.unifyFulfillmentMethod(other.fulfillmentMethod);
        const splitToHalves = this.unifySplitToHalves(other.splitToHalves);
        const orderOrigin = this.unifyOrderOrigin(other.orderOrigin);
        const matchingParameters = new InternalParameterMultiSet({
            fulfillmentMethod: fulfillmentMethod === null
                ? RSet.empty()
                : fulfillmentMethod.matchingParameters,
            splitToHalves: splitToHalves === null
                ? RSet.empty()
                : splitToHalves.matchingParameters,
            orderOrigin: orderOrigin === null ? RSet.empty() : orderOrigin.matchingParameters,
        });
        const incompatibleParameters = new InternalParameterMultiSet({
            fulfillmentMethod: (fulfillmentMethod === null || fulfillmentMethod === void 0 ? void 0 : fulfillmentMethod.type) === "failure"
                ? fulfillmentMethod.incompatibleParameters
                : RSet.empty(),
            splitToHalves: (splitToHalves === null || splitToHalves === void 0 ? void 0 : splitToHalves.type) === "failure"
                ? splitToHalves.incompatibleParameters
                : RSet.empty(),
            orderOrigin: (orderOrigin === null || orderOrigin === void 0 ? void 0 : orderOrigin.type) === "failure"
                ? orderOrigin.incompatibleParameters
                : RSet.empty(),
        });
        if (incompatibleParameters.isEmpty) {
            const value = new InternalParameterSet({
                fulfillmentMethod: (fulfillmentMethod === null || fulfillmentMethod === void 0 ? void 0 : fulfillmentMethod.type) === "success"
                    ? fulfillmentMethod.value
                    : null,
                splitToHalves: (splitToHalves === null || splitToHalves === void 0 ? void 0 : splitToHalves.type) === "success" ? splitToHalves.value : null,
                orderOrigin: (orderOrigin === null || orderOrigin === void 0 ? void 0 : orderOrigin.type) === "success" ? orderOrigin.value : null,
            });
            return { type: "success", matchingParameters, value };
        }
        return { type: "failure", matchingParameters, incompatibleParameters };
    }
    /**
     * Unites two InternalParameterSets
     *
     * Fails if predicates from one set conflict with predicates from another set
     */
    union(other) {
        var _a, _b, _c;
        const fulfillmentMethod = (_a = other.fulfillmentMethod) !== null && _a !== void 0 ? _a : this.fulfillmentMethod;
        if (this.fulfillmentMethod !== null &&
            this.fulfillmentMethod !== fulfillmentMethod) {
            return null;
        }
        const splitToHalves = (_b = other.splitToHalves) !== null && _b !== void 0 ? _b : this.splitToHalves;
        if (this.splitToHalves !== null && this.splitToHalves !== splitToHalves) {
            return null;
        }
        const orderOrigin = (_c = other.orderOrigin) !== null && _c !== void 0 ? _c : this.orderOrigin;
        if (this.orderOrigin !== null && this.orderOrigin !== orderOrigin) {
            return null;
        }
        return new InternalParameterSet({
            fulfillmentMethod,
            splitToHalves,
            orderOrigin,
        });
    }
}
